import cloneDeep from 'lodash.clonedeep';
import { MULTI_SECURITY_COMPARISON_ADDITIONAL_SECURITY_COUNT_MAX, MULTI_SECURITY_COMPARISON_ADDITIONAL_SECURITY_COUNT_MIN, } from '../features/custom-pdf-report-modal/constants';
import { PAGE_SIZE_MAX, TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR, } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/constants';
import { CustomPDFReportType } from '../enums/custom-pdf-report-type.enum';
export const hasCorrectNumberOfComparisonSecurities = (comparisonSecurities) => {
    return (comparisonSecurities.length <= MULTI_SECURITY_COMPARISON_ADDITIONAL_SECURITY_COUNT_MAX &&
        comparisonSecurities.length >= MULTI_SECURITY_COMPARISON_ADDITIONAL_SECURITY_COUNT_MIN);
};
export const hasValidReportSecurities = (reportType, primarySecurity, secondarySecurity, comparisonSecurities) => {
    let valid;
    switch (reportType) {
        case CustomPDFReportType.AGGREGATE_SECURITY_OVERVIEW:
            valid = !!primarySecurity.securityId;
            break;
        case CustomPDFReportType.AGGREGATE_SECURITY_COMPARISON:
            const secondarySecurityId = secondarySecurity?.securityId || comparisonSecurities[0]?.securityId;
            valid = !!(primarySecurity.securityId && secondarySecurityId);
            break;
        case CustomPDFReportType.MULTI_SECURITY_COMPARISON:
            valid = !!(primarySecurity.securityId && hasCorrectNumberOfComparisonSecurities(comparisonSecurities));
            break;
        default:
            valid = false;
            break;
    }
    return valid;
};
export function isCustomPDFReport(report) {
    return Object.values(CustomPDFReportType).includes(report.report_type);
}
// Keys on the (module_type, module_id) pair rather than id alone -- each concrete module table has
// its own independent id sequence, so module_id by itself can collide across module types.
export const getModuleIdentityKey = (module) => `${module.module_type}:${module.module_id ?? module.step_uuid}`;
export function getSectionsWithModules(sections, modules) {
    const newSections = cloneDeep(sections);
    const flattenedModules = modules
        .map((page, index) => {
        return page.map((module) => ({
            ...module,
            y_coordinate: module.y_coordinate * TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR + index * PAGE_SIZE_MAX,
        }));
    })
        .flat();
    if (sections.length && sections[0].modules) {
        const modulesByIdentityKey = new Map(flattenedModules.map((m) => [getModuleIdentityKey(m), m]));
        newSections.forEach((section) => {
            const sectionModules = [];
            section.modules.forEach((sectionModule) => {
                const module = modulesByIdentityKey.get(getModuleIdentityKey(sectionModule));
                if (module) {
                    sectionModules.push(module);
                }
            });
            // eslint-disable-next-line no-param-reassign
            section.modules = sectionModules;
        });
    }
    return newSections;
}
/**
 * Returns whether any module in `modules` isn't assigned to any of `sections`. A report with no
 * sections has nothing for a module to be orphaned from, so this returns false in that case.
 */
export function hasOrphanModules(sections, modules) {
    if (!sections.length) {
        return false;
    }
    const flattenedModules = modules.flat();
    const sectionedModuleKeys = new Set(sections.flatMap((section) => section.modules ?? []).map(getModuleIdentityKey));
    return flattenedModules.some((module) => !sectionedModuleKeys.has(getModuleIdentityKey(module)));
}
export function getSortedSections(sections) {
    return [...sections].sort((a, b) => {
        // Get the first module for sections A and B
        const minA = a.modules.reduce((min, m) => {
            if (m.y_coordinate === min.y_coordinate
                ? m.x_coordinate < min.x_coordinate
                : m.y_coordinate < min.y_coordinate) {
                return m;
            }
            return min;
        });
        const minB = b.modules.reduce((min, m) => {
            if (m.y_coordinate === min.y_coordinate
                ? m.x_coordinate < min.x_coordinate
                : m.y_coordinate < min.y_coordinate) {
                return m;
            }
            return min;
        });
        // Compare by page, then y_coordinate
        if (minA.y_coordinate !== minB.y_coordinate) {
            return minA.y_coordinate - minB.y_coordinate;
        }
        return minA.x_coordinate - minB.x_coordinate;
    });
}
